/**
 * @quotech/ui - Design Tokens
 *
 * ⚠️ COLOURS HERE ARE DEPRECATED-ON-CONTACT (2026-08-20).
 *
 * The `colors` export below is NOT the arbiter of theme colour and never was in
 * practice. It hardcodes `brand.primary: '#2563eb'` and `background.dark:
 * '#0f172a'`, which agree with neither the master shell's CSS variables
 * (`--background: #0a0a0a`) nor the hub apps' HSL triplets. It was one of four
 * competing colour systems, and having four is why Job Details drifted to 617
 * raw palette classes against 16 semantic ones.
 *
 * For colour, use the CSS custom properties instead, consumed through Tailwind
 * utilities: the canonical set in each app's `index.css` plus the additive
 * layer in `packages/ui/src/styles/tokens.css` (surface elevation ramp, brand
 * accent, semantic status, categorical accents).
 *
 * Do not add new colour entries here and do not wire new consumers to
 * `colors`. Existing consumers are left in place deliberately: removing them is
 * a separate blast radius, not a drive-by. The non-colour sections
 * (typography, spacing, breakpoints, z-index, animation) remain useful.
 *
 * PURPOSE: Centralized design tokens for consistent styling across ALL Quotech modules
 *
 * These tokens are the source of truth for:
 * - Colors (brand, semantic, chart)
 * - Typography (fonts, sizes, weights)
 * - Spacing (sidebar, header, page)
 * - Breakpoints (responsive design)
 * - Z-Index (layering)
 * - Animations
 */

// ============================================================================
// COLOR TOKENS
// ============================================================================

export const colors = {
  // Brand colors
  brand: {
    primary: '#2563eb',       // Quotech Blue
    primaryHover: '#1d4ed8',
    primaryLight: '#dbeafe',
    secondary: '#8b5cf6',     // Purple accent
    secondaryHover: '#7c3aed',
  },

  // Semantic colors
  semantic: {
    success: '#22c55e',
    successLight: '#dcfce7',
    warning: '#f59e0b',
    warningLight: '#fef3c7',
    error: '#ef4444',
    errorLight: '#fee2e2',
    info: '#3b82f6',
    infoLight: '#dbeafe',
  },

  // Background colors
  background: {
    light: '#ffffff',
    dark: '#0f172a',
    surfaceLight: '#f8fafc',
    surfaceDark: '#1e293b',
    mutedLight: '#f1f5f9',
    mutedDark: '#334155',
  },

  // Text colors
  text: {
    primaryLight: '#1e293b',
    primaryDark: '#f1f5f9',
    secondaryLight: '#64748b',
    secondaryDark: '#94a3b8',
    mutedLight: '#94a3b8',
    mutedDark: '#64748b',
  },

  // Border colors
  border: {
    light: '#e2e8f0',
    dark: '#334155',
    focusLight: '#2563eb',
    focusDark: '#60a5fa',
  },

  // Chart colors (for data visualization)
  chart: [
    '#2563eb', // Blue
    '#8b5cf6', // Purple
    '#22c55e', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#06b6d4', // Cyan
    '#ec4899', // Pink
    '#14b8a6', // Teal
  ],
}

// Tailwind class mappings for components
export const colorClasses = {
  positive: {
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-200 dark:border-emerald-800',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  negative: {
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  },
  warning: {
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  },
  neutral: {
    text: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-50 dark:bg-slate-800',
    border: 'border-slate-200 dark:border-slate-700',
    badge: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
  },
  primary: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  },
}

// ============================================================================
// TYPOGRAPHY TOKENS
// ============================================================================

export const typography = {
  fonts: {
    sans: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
  },

  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },

  weights: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },

  lineHeights: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },
}

// Tailwind typography classes
export const typographyClasses = {
  h1: 'text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50',
  h2: 'text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50',
  h3: 'text-xl font-semibold text-slate-800 dark:text-slate-100',
  h4: 'text-lg font-medium text-slate-700 dark:text-slate-200',
  body: 'text-sm text-slate-600 dark:text-slate-400',
  bodyLg: 'text-base text-slate-600 dark:text-slate-400',
  bodySm: 'text-xs text-slate-500 dark:text-slate-500',
  label: 'text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400',
  caption: 'text-xs text-slate-400 dark:text-slate-500',
  metric: 'text-2xl font-bold text-slate-900 dark:text-slate-50',
  metricLg: 'text-3xl font-bold text-slate-900 dark:text-slate-50',
  metricSm: 'text-xl font-bold text-slate-900 dark:text-slate-50',
}

// ============================================================================
// SPACING TOKENS
// ============================================================================

export const spacing = {
  // Layout dimensions - UNIVERSAL ACROSS ALL MODULES
  sidebar: {
    expanded: '256px',  // 16rem
    collapsed: '64px',  // 4rem
    expandedNum: 256,
    collapsedNum: 64,
  },

  header: {
    height: '64px',     // 4rem
    heightNum: 64,
  },

  page: {
    padding: '24px',    // 1.5rem
    paddingNum: 24,
  },

  // Component spacing
  card: '16px',
  cardCompact: '12px',
  
  // Gaps
  gapXs: '4px',
  gapSm: '8px',
  gapMd: '16px',
  gapLg: '24px',
  gapXl: '32px',
}

// Tailwind spacing classes
export const spacingClasses = {
  page: 'p-6',
  section: 'p-4',
  card: 'p-4',
  cardCompact: 'p-3',
  gapLg: 'gap-6',
  gapMd: 'gap-4',
  gapSm: 'gap-2',
  gapXs: 'gap-1',
  stack: 'space-y-4',
  stackLg: 'space-y-6',
  stackSm: 'space-y-2',
}

// ============================================================================
// BREAKPOINT TOKENS
// ============================================================================

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',

  // Numeric values for JS usage
  smNum: 640,
  mdNum: 768,
  lgNum: 1024,
  xlNum: 1280,
  '2xlNum': 1536,
}

// ============================================================================
// Z-INDEX TOKENS
// ============================================================================

// Canonical z-index scale — keep in sync with lib/designTokens.ts
// Layer 1 (z-10): Content-level sticky elements (table headers)
// Layer 2 (z-20): Elevated sticky (pinned columns)
// Layer 3 (z-30): Sidebar & fixed nav
// Layer 4 (z-40): Backdrops & overlays
// Layer 5 (z-50): Modals, dropdowns, tooltips, command palette
// Layer 6 (100): Toasts. A toast raised from inside a dialog must paint over
//   its overlay; at an equal z the later-mounted dialog portal wins.
// Layer 7 (z-[9999]): Sidebar flyout menus (portaled above everything)
export const zIndex = {
  sticky: 10,
  pinnedColumn: 20,
  sidebar: 30,
  overlay: 40,
  modal: 50,
  dropdown: 50,
  header: 50,
  toast: 100,
  commandPalette: 50,
  tooltip: 50,
}

// ============================================================================
// ANIMATION TOKENS
// ============================================================================

export const animation = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },

  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
}

// Tailwind animation classes
export const animationClasses = {
  fadeIn: 'animate-in fade-in duration-200',
  slideUp: 'animate-in slide-in-from-bottom-2 duration-200',
  slideDown: 'animate-in slide-in-from-top-2 duration-200',
  scaleIn: 'animate-in zoom-in-95 duration-200',
  transition: 'transition-all duration-200',
  transitionFast: 'transition-all duration-150',
  transitionSlow: 'transition-all duration-300',
}

// ============================================================================
// COMPONENT STYLING TOKENS
// ============================================================================

export const cardClasses = {
  base: 'rounded-lg border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800',
  interactive: 'rounded-lg border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm transition-all cursor-pointer',
  elevated: 'rounded-lg border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm',
  selected: 'rounded-lg border-2 bg-white dark:bg-slate-900 border-blue-500 dark:border-blue-400 shadow-sm',
}

export const tableClasses = {
  header: 'bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 text-xs font-medium uppercase tracking-wide',
  cell: 'text-sm text-slate-700 dark:text-slate-300',
  row: 'border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50',
  rowAlternate: 'bg-slate-50/50 dark:bg-slate-800/30',
}

export const buttonClasses = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
  secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600',
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
}

// ============================================================================
// EXPORT ALL TOKENS
// ============================================================================

const tokens = {
  colors,
  colorClasses,
  typography,
  typographyClasses,
  spacing,
  spacingClasses,
  breakpoints,
  zIndex,
  animation,
  animationClasses,
  cardClasses,
  tableClasses,
  buttonClasses,
}

export default tokens

